import { useState } from "react";
import {
  CloudOff,
  LoaderCircle,
  RefreshCw,
  Sailboat,
  UserPlus
} from "lucide-react";

import type {
  AppNavigation,
  AppPersistenceMode
} from "@/app/integration/contracts";
import type { ChildProfile } from "@/domain";
import { childProfileSchema } from "@/domain/schemas";
import type {
  ProfileSessionSaveStatus,
  ProfileSessionStatus
} from "@/features/navigation";
import {
  ProfileCreateDialog,
  ProfilePicker,
  type CreateProfileInput
} from "@/features/profiles";
import { PrimaryCommandButton } from "@/ui";

import "./onboarding.css";

export interface OnboardingScreenProps {
  readonly collectionError: Error | null;
  readonly collectionStatus: ProfileSessionStatus;
  readonly navigation: AppNavigation;
  readonly onRetryProfiles: () => void;
  readonly onSaveAndSelectProfile: (
    profile: ChildProfile
  ) => Promise<boolean>;
  readonly onSelectProfile: (profile: ChildProfile) => Promise<boolean>;
  readonly persistenceMode: AppPersistenceMode;
  readonly profiles: readonly ChildProfile[];
  readonly saveError: Error | null;
  readonly saveStatus: ProfileSessionSaveStatus;
  readonly selectedProfile: ChildProfile | null;
}

function createProfileCandidate(input: CreateProfileInput): ChildProfile {
  return {
    id: "profile-" + globalThis.crypto.randomUUID(),
    nickname: input.nickname,
    avatarId: input.avatarId,
    createdAt: new Date().toISOString()
  };
}

export function OnboardingScreen({
  collectionError,
  collectionStatus,
  navigation,
  onRetryProfiles,
  onSaveAndSelectProfile,
  onSelectProfile,
  persistenceMode,
  profiles,
  saveError,
  saveStatus,
  selectedProfile
}: OnboardingScreenProps) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const collectionFailed =
    collectionStatus === "error" || collectionError !== null;
  const saveFailed = saveStatus === "error" || saveError !== null;
  const isSaving = saveStatus === "saving";

  async function selectProfile(profile: ChildProfile) {
    setSelectionError(null);
    const selected = await onSelectProfile(profile);

    if (!selected) {
      setSelectionError("这位小船员还没上船，再选一次吧。");
      return;
    }

    navigation.showMap();
  }

  function openCreateDialog() {
    setCandidateError(null);
    setSelectionError(null);
    setIsCreateOpen(true);
  }

  function closeCreateDialog() {
    if (!isSaving) {
      setCandidateError(null);
      setIsCreateOpen(false);
    }
  }

  async function createProfile(input: CreateProfileInput) {
    const parsedProfile = childProfileSchema.safeParse(
      createProfileCandidate(input)
    );

    if (!parsedProfile.success) {
      setCandidateError("这个小名还不能用。换一个短短的小名，再试试。");
      return;
    }

    setCandidateError(null);
    const saved = await onSaveAndSelectProfile(parsedProfile.data);

    if (saved) {
      setIsCreateOpen(false);
    }
  }

  return (
    <section className="onboarding-screen">
      <div className="onboarding-screen__inner">
        <header className="onboarding-screen__intro">
          <p className="onboarding-screen__kicker">
            <Sailboat aria-hidden="true" />
            准备好，向一年级出发
          </p>
          <h1>今天谁来启航？</h1>
          <p className="onboarding-screen__subtitle">
            学校岛的晨灯已经亮起，每位小船员都有自己的发现。
          </p>

          {persistenceMode === "temporary" ? (
            <aside
              aria-live="polite"
              className="onboarding-screen__temporary"
              role="status"
            >
              <CloudOff aria-hidden="true" />
              <span>
                <strong>临时启航</strong>
                关闭或刷新页面后，小船员资料和进度不会留下。
              </span>
            </aside>
          ) : null}
        </header>

        <div className="onboarding-screen__profiles">
          {collectionStatus === "loading" && !collectionFailed ? (
            <div
              aria-live="polite"
              className="onboarding-screen__state"
              role="status"
            >
              <LoaderCircle
                aria-hidden="true"
                className="onboarding-screen__spinner"
              />
              <strong>正在准备小船员名单</strong>
            </div>
          ) : null}

          {collectionFailed ? (
            <div className="onboarding-screen__state" role="alert">
              <CloudOff aria-hidden="true" />
              <strong>小船员名单暂时打不开</strong>
              <span>再试一次，或者请家长稍后回来看看。</span>
              <button
                className="onboarding-screen__retry"
                onClick={onRetryProfiles}
                type="button"
              >
                <RefreshCw aria-hidden="true" />
                再试一次
              </button>
            </div>
          ) : null}

          {collectionStatus === "ready" &&
          !collectionFailed &&
          profiles.length === 0 ? (
            <section
              aria-labelledby="onboarding-empty-title"
              className="onboarding-screen__empty"
            >
              <div className="onboarding-screen__empty-mark" aria-hidden="true">
                <Sailboat />
              </div>
              <p className="profile-picker__eyebrow">启航名单</p>
              <h2 id="onboarding-empty-title">还没有小船员</h2>
              <p>第一张启航证正在等你。</p>
              <PrimaryCommandButton icon={UserPlus} onClick={openCreateDialog}>
                新建小船员
              </PrimaryCommandButton>
            </section>
          ) : null}

          {collectionStatus === "ready" &&
          !collectionFailed &&
          profiles.length > 0 ? (
            <>
              {selectionError === null ? null : (
                <div className="onboarding-screen__state" role="alert">
                  <CloudOff aria-hidden="true" />
                  <strong>{selectionError}</strong>
                </div>
              )}
              <ProfilePicker
                onCreate={openCreateDialog}
                onSelect={(profile) => void selectProfile(profile)}
                profiles={profiles}
                selectedProfileId={selectedProfile?.id ?? null}
              />
            </>
          ) : null}
        </div>
      </div>

      {isCreateOpen ? (
        <ProfileCreateDialog
          busy={isSaving}
          errorMessage={
            candidateError ??
            (saveFailed
              ? "小船员资料还没收好，再试一次吧。"
              : null)
          }
          onCancel={closeCreateDialog}
          onCreate={(input) => void createProfile(input)}
        />
      ) : null}
    </section>
  );
}
